import { useMemo } from 'react';
import { Select, FormField } from '@cloudscape-design/components';

interface TimezoneSelectProps {
  value: string;
  onChange: (timezone: string) => void;
  error?: string;
}

// Pinned NZ/AU timezones shown at the top of the dropdown
const PINNED_TIMEZONES = [
  'Pacific/Auckland',
  'Australia/Sydney',
  'Australia/Melbourne',
  'Australia/Brisbane',
  'Australia/Perth',
  'Australia/Adelaide',
  'Australia/Darwin',
  'Australia/Hobart',
  'Pacific/Chatham',
];

/**
 * Searchable IANA timezone dropdown.
 * Pre-populated with all valid IANA timezones, with NZ/AU pinned at top.
 * Stores the canonical IANA string as the value.
 *
 * Requirements: 9.1, 9.2, 9.3, 9.4, 9.5, 9.6, 9.7
 */
export function TimezoneSelect({ value, onChange, error }: TimezoneSelectProps) {
  const options = useMemo(() => {
    // Build pinned group
    const pinnedOptions = PINNED_TIMEZONES.map((tz) => ({
      label: tz,
      value: tz,
    }));

    // Common IANA timezones (subset — full list would be very large)
    const commonTimezones = [
      'Pacific/Auckland', 'Pacific/Chatham', 'Pacific/Fiji',
      'Australia/Sydney', 'Australia/Melbourne', 'Australia/Brisbane',
      'Australia/Perth', 'Australia/Adelaide', 'Australia/Darwin', 'Australia/Hobart',
      'Asia/Tokyo', 'Asia/Shanghai', 'Asia/Singapore', 'Asia/Kolkata',
      'Asia/Dubai', 'Asia/Hong_Kong',
      'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Moscow',
      'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
      'America/Toronto', 'America/Vancouver',
      'US/Eastern', 'US/Central', 'US/Mountain', 'US/Pacific',
      'UTC',
    ];

    // Build remaining options (exclude pinned ones)
    const pinnedSet = new Set(PINNED_TIMEZONES);
    const otherOptions = commonTimezones
      .filter((tz: string) => !pinnedSet.has(tz))
      .map((tz: string) => ({ label: tz, value: tz }));

    return [
      { label: 'Common (NZ/AU)', options: pinnedOptions },
      { label: 'Other Timezones', options: otherOptions },
    ];
  }, []);

  const selectedOption = value ? { label: value, value } : null;

  return (
    <FormField label="Timezone" errorText={error}>
      <Select
        selectedOption={selectedOption}
        onChange={({ detail }) => onChange(detail.selectedOption?.value ?? '')}
        options={options}
        filteringType="auto"
        placeholder="Search timezones..."
        empty="No matching timezone found"
      />
    </FormField>
  );
}
